/**
 * RGM Spotlight · Phase 2 — A&R Reviewer Workspace.
 * Route: /spotlight-ar/:spotlightId
 *
 * Uses the CANONICAL RGM PlayerProvider — no standalone <audio>.
 * Reuses the shared ScoringPanel component from Contest Judging
 * Phase 1 (when the CEO configures an A&R rubric for the week).
 * Adds the A&R-specific YES/MAYBE/NO recommendation + Top-2 nominee
 * toggle. Reviewer sees their own review only; peers are private.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";
import ScoringPanel from "@/components/contest/ScoringPanel";
import RecommendationPicker from "@/components/judging/RecommendationPicker";

const _buildPlayerPayload = (sub) => {
  if (!sub || !sub.submission_id) return null;
  return {
    submission_id: sub.submission_id,
    trackId: sub.submission_id,
    track_title: sub.track_title || "Untitled track",
    artist_name: sub.artist_name || "Unknown artist",
    cover_url: sub.cover_art || "",
  };
};

export default function SpotlightARWorkspace() {
  const { spotlightId } = useParams();
  const navigate = useNavigate();
  const player = usePlayer();
  const [state, setState] = useState(null);
  const [i, setI] = useState(0);
  const [rec, setRec] = useState(null);
  const [nominee, setNominee] = useState(false);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/ar/spotlights/${spotlightId}`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load A&R workspace");
      navigate("/judge");
    }
  };
  useEffect(() => { load(); }, [spotlightId]); // eslint-disable-line

  const submissions = useMemo(() => state?.submissions || [], [state]);
  const criteria = state?.config?.criteria || [];
  const scale = state?.config?.scale || { min: 1, max: 10 };
  const cur = submissions[i];
  const existing = cur && (state?.my_reviews?.[cur.submission_id] || null);
  const locked = !!state?.spotlight?.ar_review_closed_at;
  const nomineeUsed = state?.nominee_used || 0;
  const nomineeCap = state?.nominee_cap || 2;

  useEffect(() => {
    if (!cur) return;
    setRec(existing?.recommendation || null);
    setNominee(!!existing?.is_nominee);
    setScores(existing?.scores || {});
    setNotes(existing?.notes || "");
  }, [cur, existing?.review_id]); // eslint-disable-line

  const done = useMemo(() => (
    submissions.filter((s) => (state?.my_reviews?.[s.submission_id]?.submitted_at)).length
  ), [submissions, state]);

  const payload = useMemo(() => _buildPlayerPayload(cur), [cur]);
  const currentTrackId = player?.track?.trackId || null;
  const isThisPlaying = !!payload && currentTrackId === payload.trackId && player.isPlaying;

  const togglePlay = () => {
    if (!payload) { toast.error("No audio available."); return; }
    if (currentTrackId === payload.trackId) { player.toggle(); return; }
    player.play(payload, { sourceLabel: "🎧 A&R Review" });
  };

  const persist = async (submit) => {
    if (!cur) return;
    if (submit) {
      if (!rec) { toast.error("Pick YES, MAYBE or NO"); return; }
      if (criteria.length && state?.config?.require_scores) {
        for (const c of criteria) {
          if (scores[c.criterion_id] == null) {
            toast.error(`Missing score for ${c.label}`); return;
          }
        }
      }
    }
    setSaving(true);
    try {
      await api.put(`/ar/spotlights/${spotlightId}/submissions/${cur.submission_id}/review`, {
        recommendation: rec, is_nominee: nominee,
        scores: criteria.length ? scores : {},
        notes, submit,
      });
      toast.success(submit ? "Review submitted" : "Draft saved");
      await load();
      if (submit) setI(Math.min(i + 1, submissions.length - 1));
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Could not save"));
    } finally { setSaving(false); }
  };

  if (!state) return <div className="min-h-screen bg-black text-white flex items-center justify-center text-xs text-gray-500" data-testid="ar-workspace-loading">Loading…</div>;
  if (submissions.length === 0) return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="ar-workspace-empty">
      <button onClick={() => navigate("/judge")} className="text-fuchsia-300 text-sm">← Inbox</button>
      <p className="mt-4 text-sm text-gray-400">No submissions to review in this Spotlight cycle.</p>
    </div>
  );

  const willAddNominee = nominee && !existing?.is_nominee;
  const nomineeCounter = `${nomineeUsed}${willAddNominee ? "+1" : ""}/${nomineeCap}`;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="ar-workspace-page">
      <header className="flex items-center gap-2 px-3 h-12 border-b border-white/10 bg-[#0a0a10]">
        {/* Phase 1 — permanent deterministic exit back to RGM Studio
            Home (`/home`, the same canonical route the top-level `/`
            RootRoute redirects to). Mirrors the approved Contest
            Judging ← Home control (commit b926b38). */}
        <button
          onClick={() => navigate("/home")}
          className="text-gray-300 hover:text-white text-sm"
          data-testid="ar-workspace-home"
          aria-label="Exit Spotlight A&R and go to RGM Studio Home"
        >← Home</button>
        <button onClick={() => navigate("/judge")} className="text-fuchsia-300 text-sm" data-testid="ar-workspace-back">← Inbox</button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold truncate">{state.spotlight.week_label || `Spotlight ${spotlightId}`}</p>
          <p className="text-[10px] text-gray-500">Item {i + 1} of {submissions.length} · {done} submitted · Top-2 {nomineeCounter}</p>
        </div>
        {locked && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid="ar-workspace-locked-badge">A&R closed</span>}
      </header>

      <main className="flex-1 overflow-y-auto px-3 pt-4 pb-24 max-w-2xl mx-auto w-full">
        {existing?.submitted_at && (
          <p className="mb-2 text-[10px] text-emerald-300" data-testid="ar-review-submitted-badge">✓ Submitted {new Date(existing.submitted_at).toLocaleString()}</p>
        )}

        <div className="rounded border border-white/10 bg-white/[0.02] p-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">Submission #{i + 1}</p>
          <p className="text-sm font-bold text-white mt-1" data-testid="ar-submission-title">
            {cur.track_title || cur.submission_id.slice(0, 8)}
          </p>
          {cur.artist_name && <p className="text-[11px] text-gray-400">{cur.artist_name}</p>}
          {payload ? (
            <button
              type="button" onClick={togglePlay}
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-bold"
              data-testid="ar-submission-play-btn"
              aria-label={isThisPlaying ? "Pause submission" : "Play submission"}
            >
              <span aria-hidden="true">{isThisPlaying ? "⏸" : "▶"}</span>
              <span>{isThisPlaying ? "Pause" : "Play submission"}</span>
            </button>
          ) : (
            <p className="text-[11px] text-amber-300 mt-2" data-testid="ar-submission-no-audio">No audio available.</p>
          )}
        </div>

        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1">Your recommendation</p>
          <RecommendationPicker value={rec} onChange={setRec} disabled={locked || !!existing?.submitted_at} />
        </div>

        <label className="mt-4 flex items-center gap-2 text-xs" data-testid="ar-nominee-row">
          <input
            type="checkbox" checked={nominee}
            onChange={(e) => setNominee(e.target.checked)}
            disabled={locked || !!existing?.submitted_at || (nomineeUsed >= nomineeCap && !existing?.is_nominee && !nominee)}
            data-testid="ar-nominee-checkbox"
            className="accent-fuchsia-500"
          />
          <span>🌟 Nominate for Top {nomineeCap}
            <span className="text-gray-500 ml-1">({nomineeCounter} used)</span>
          </span>
        </label>

        {criteria.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1">
              Rubric{state?.config?.require_scores ? "" : " (optional)"}
            </p>
            <ScoringPanel
              criteria={criteria} scale={scale}
              scores={scores} notes={notes}
              disabled={locked || !!existing?.submitted_at}
              onScoreChange={(cid, v) => setScores((prev) => ({ ...prev, [cid]: v }))}
              onNotesChange={setNotes}
            />
          </div>
        )}
        {criteria.length === 0 && (
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-bold block mb-1">Private A&R notes (optional)</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
              rows={4} disabled={locked || !!existing?.submitted_at}
              placeholder="Only you and admin will see these notes."
              className="w-full bg-black/40 border border-white/15 rounded p-2 text-xs text-white focus:outline-none focus:border-fuchsia-400/70 disabled:opacity-50"
              data-testid="ar-notes-input"
            />
          </div>
        )}
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-white/10 bg-[#0a0a10]" data-testid="ar-workspace-transport">
        <div className="max-w-2xl mx-auto flex items-center gap-2 px-3 py-2">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="text-gray-300 hover:text-white text-sm disabled:opacity-30" data-testid="ar-prev-btn">← Prev</button>
          <button
            onClick={() => persist(false)} disabled={saving || locked || !!existing?.submitted_at}
            className="px-3 py-1.5 rounded border border-white/20 text-xs font-bold hover:bg-white/5 disabled:opacity-30"
            data-testid="ar-save-draft-btn"
          >Save draft</button>
          <button
            onClick={() => persist(true)} disabled={saving || locked || !!existing?.submitted_at}
            className="px-3 py-1.5 rounded bg-fuchsia-600 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-30 flex-1"
            data-testid="ar-submit-btn"
          >{existing?.submitted_at ? "Submitted ✓" : "Submit review"}</button>
          <button onClick={() => setI(Math.min(submissions.length - 1, i + 1))} disabled={i >= submissions.length - 1} className="text-gray-300 hover:text-white text-sm disabled:opacity-30" data-testid="ar-next-btn">Next →</button>
        </div>
      </nav>
    </div>
  );
}
